import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Entitlements,
  BillingSubscription,
  PlanType,
  getPlanFeatures,
  PLAN_FEATURES
} from '@/types/entitlements';

// Global circuit breaker state to survive component remounts
let globalLastFetchTime = 0;
let globalFetchCount = 0;
let globalFetchPromise: Promise<void> | null = null;
const ENTITLEMENTS_FETCH_LIMIT = 3; // Reduced from 10 to prevent runaway loops
const ENTITLEMENTS_FETCH_WINDOW = 10000; // 10 seconds (increased from 5s)

interface EntitlementsContextType {
  entitlements: Entitlements | null;
  billing: BillingSubscription | null;
  loading: boolean;
  error: string | null;
  refreshEntitlements: () => Promise<void>;
  refreshBilling: () => Promise<void>;
  // NEW: Stripe Hosted Checkout methods
  createCheckoutSession: (priceId: string) => Promise<{ url: string; sessionId?: string }>;
  createPortalSession: (returnUrl?: string) => Promise<{ url: string }>;
  // Legacy method - kept for backwards compatibility but deprecated
  upgradeToProPlan: (plan: 'pro', billing: 'monthly' | 'annual', paymentMethodId: string) => Promise<{ clientSecret?: string; subscriptionId?: string }>;
  cancelSubscription: (cancelAtPeriodEnd?: boolean) => Promise<void>;
  canUseFeature: (feature: keyof Entitlements) => boolean;
  getCurrentPlanFeatures: () => any;
  isProUser: boolean;
  isEventRoomEnabled: boolean;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

interface EntitlementsProviderProps {
  children: ReactNode;
}

export function EntitlementsProvider({ children }: EntitlementsProviderProps) {
  const { user, initialized } = useAuth();
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [billing, setBilling] = useState<BillingSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Circuit breaker state - Global to survive remounts (defined at module level)

  // Track mount status
  useEffect(() => {
    console.log('EntitlementsProvider: MOUNTED');
    return () => console.log('EntitlementsProvider: UNMOUNTED');
  }, []);

  const fetchEntitlements = async () => {
    // circuit breaker check
    const now = Date.now();

    // Reset if window passed
    if (now - globalLastFetchTime > ENTITLEMENTS_FETCH_WINDOW) {
      globalFetchCount = 0;
      globalLastFetchTime = now;
    }

    // Check limit
    if (globalFetchCount >= ENTITLEMENTS_FETCH_LIMIT) {
      if (globalFetchCount === ENTITLEMENTS_FETCH_LIMIT) {
        console.warn('EntitlementsContext: Circuit breaker activated. Too many requests. Pausing updates.');
        globalFetchCount++; // Increment once more to silence subsequent warnings
      }
      return;
    }

    globalFetchCount++;

    if (!user) {
      setEntitlements(null);
      setBilling(null);
      setLoading(false);
      return;
    }

    // Request deduplication
    if (globalFetchPromise) {
      console.log('EntitlementsContext: Request already in progress, reusing promise');
      try {
        await globalFetchPromise;
      } catch (e) {
        // ignore error from shared promise, existing logic handles state
      }
      return;
    }

    if (!user) {
      setEntitlements(null);
      setBilling(null);
      setLoading(false);
      return;
    }

    // Wrap in IIFE to assign to global promise
    globalFetchPromise = (async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching entitlements for user:', user.id);

        // Fetch entitlements
        const { data: entitlementsData, error: entitlementsError } = await supabase
          .from('entitlements')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(); // Use maybeSingle instead of single to avoid errors

        if (entitlementsError && entitlementsError.code !== 'PGRST116') {
          throw entitlementsError;
        }

        // Fetch billing subscription
        const { data: billingData, error: billingError } = await supabase
          .from('billing_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle(); // Use maybeSingle instead of single to avoid errors

        if (billingError && billingError.code !== 'PGRST116') {
          console.warn('Billing subscription not found:', billingError);
        }

        // Set default entitlements if none exist (starter plan)
        if (!entitlementsData) {
          const defaultEntitlements: Entitlements = {
            id: '',
            user_id: user.id,
            plan: 'starter',
            billing: 'monthly',
            calendar_integrations: false,
            priority_support: false,
            analytics_tier: 'basic',
            event_room: false,
            max_participants: 3,
            analytics_retention_days: 14,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setEntitlements(defaultEntitlements);
        } else {
          setEntitlements(entitlementsData);
        }
        setBilling(billingData);
      } catch (err) {
        console.error('Error fetching entitlements:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch entitlements');
      } finally {
        setLoading(false);
      }
    })();

    try {
      await globalFetchPromise;
    } finally {
      globalFetchPromise = null;
    }
  };

  const refreshEntitlements = async () => {
    await fetchEntitlements();
  };

  const refreshBilling = async () => {
    if (!user) return;

    try {
      // Fetch latest billing subscription from database
      const { data: billingData, error: billingError } = await supabase
        .from('billing_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (billingError && billingError.code !== 'PGRST116') {
        console.warn('Error fetching billing:', billingError);
      }

      setBilling(billingData || null);

      // Refresh entitlements to sync with billing
      await fetchEntitlements();
    } catch (err) {
      console.error('Error refreshing billing:', err);
    }
  };

  // NEW: Create Stripe Hosted Checkout session
  const createCheckoutSession = async (priceId: string): Promise<{ url: string; sessionId?: string }> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing/create-checkout-session', {
        body: {
          price_id: priceId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to create checkout session');
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      return {
        url: data.url,
        sessionId: data.session_id,
      };
    } catch (err) {
      console.error('Error creating checkout session:', err);
      throw err;
    }
  };

  // NEW: Create Stripe Customer Portal session
  const createPortalSession = async (returnUrl?: string): Promise<{ url: string }> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      console.log('[Entitlements] Creating portal session for user:', user.id);

      const { data, error } = await supabase.functions.invoke('stripe-billing/customer-portal', {
        body: returnUrl ? { return_url: returnUrl } : {},
      });

      console.log('[Entitlements] Portal session response:', { data, error });

      if (error) {
        console.error('[Entitlements] Supabase function error:', error);
        throw new Error(error.message || 'Failed to create portal session');
      }

      // Check if the response contains an error from the edge function
      if (data?.error) {
        console.error('[Entitlements] Edge function returned error:', data.error);
        throw new Error(data.error);
      }

      if (!data?.url) {
        console.error('[Entitlements] No URL in response:', data);
        throw new Error('No portal URL returned. Please ensure Stripe Customer Portal is configured.');
      }

      return { url: data.url };
    } catch (err) {
      console.error('[Entitlements] Error creating portal session:', err);
      throw err;
    }
  };

  // Legacy method - kept for backwards compatibility
  // @deprecated Use createCheckoutSession instead
  const upgradeToProPlan = async (
    plan: 'pro',
    billing: 'monthly' | 'annual',
    paymentMethodId: string
  ): Promise<{ clientSecret?: string; subscriptionId?: string }> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.warn('upgradeToProPlan is deprecated. Use createCheckoutSession instead.');

    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing/create-subscription', {
        body: {
          plan,
          billing,
          payment_method_id: paymentMethodId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to create subscription');
      }

      // Refresh billing and entitlements after successful subscription creation
      await refreshBilling();

      return {
        clientSecret: data?.client_secret,
        subscriptionId: data?.subscription_id,
      };
    } catch (err) {
      console.error('Error upgrading to Pro plan:', err);
      throw err;
    }
  };

  const cancelSubscription = async (cancelAtPeriodEnd: boolean = true): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { error } = await supabase.functions.invoke('stripe-billing/update-subscription', {
        body: {
          cancel_at_period_end: cancelAtPeriodEnd,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to cancel subscription');
      }

      // Refresh billing and entitlements after cancellation
      await refreshBilling();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      throw err;
    }
  };

  // Check if user can use a specific feature
  const canUseFeature = (feature: keyof Entitlements): boolean => {
    if (!entitlements) return false;

    switch (feature) {
      case 'calendar_integrations':
        return entitlements.calendar_integrations;
      case 'priority_support':
        return entitlements.priority_support;
      case 'analytics_tier':
        return entitlements.analytics_tier === 'advanced';
      case 'event_room':
        return entitlements.event_room;
      case 'max_participants':
        return entitlements.max_participants > 3;
      default:
        return false;
    }
  };

  // Get current plan features
  const getCurrentPlanFeatures = () => {
    if (!entitlements) return PLAN_FEATURES.starter;

    return getPlanFeatures(entitlements.plan, entitlements.event_room);
  };

  // Computed properties
  const isProUser = entitlements?.plan === 'pro';
  const isEventRoomEnabled = entitlements?.event_room === true;

  // Fetch entitlements when user changes
  useEffect(() => {
    if (initialized) {
      fetchEntitlements();
    }
  }, [user?.id, initialized]);

  // Set up real-time subscription for entitlements changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('entitlements_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'entitlements',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchEntitlements();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'billing_subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchEntitlements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const value: EntitlementsContextType = {
    entitlements,
    billing,
    loading,
    error,
    refreshEntitlements,
    refreshBilling,
    createCheckoutSession,
    createPortalSession,
    upgradeToProPlan,
    cancelSubscription,
    canUseFeature,
    getCurrentPlanFeatures,
    isProUser,
    isEventRoomEnabled
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  if (context === undefined) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
}

// Hook for checking specific feature access
export function useFeatureAccess(feature: keyof Entitlements) {
  const { canUseFeature, loading } = useEntitlements();
  return {
    hasAccess: canUseFeature(feature),
    loading
  };
}

// Hook for plan-based UI rendering
export function usePlanFeatures() {
  const { getCurrentPlanFeatures, loading, isProUser, isEventRoomEnabled } = useEntitlements();
  return {
    features: getCurrentPlanFeatures(),
    loading,
    isProUser,
    isEventRoomEnabled
  };
}
